import { useCallback } from 'react';

import { apiService } from '../../services/api.services';
import { useVidaProtocolAtendimentoStore } from '../../store/vida/vidaProtocolServiceStore';
import type { VinculoEstipulanteItem } from '../../types/estipulanteVinculoType';
import type { UserPoliciesListItem } from '../../types/serviceType';

/** Digit of line in the legacy key / estipulante vinculo: 3=Health, 4=Vida, 5=Previdence (`parsed.ramoCodigo`). */
export const VIDA_APOLICE_RAMO_CODE = 4;

/** Ramo of the vinculo chosen in CompanyPage (store / `parsed`); not confuse with `codRamoApol` from the list of policies (e.g.: 726 in the API). */
export type EstipulanteRamoForProtocol = {
  codigo?: string;
  nome?: string;
};

/** Options of {@link useCompanyVidaProtocolAtendimento}; `skipRemoteProtocolRequest` avoids the GET after reload with auto-selection. */
export type SyncProtocolAfterPolicyRowOptions = {
  skipRemoteProtocolRequest?: boolean;
  vinculoItem?: VinculoEstipulanteItem;
};

function nomeIndicaVida(nome: string | null | undefined): boolean {
  const n = String(nome ?? '')
    .trim()
    .toLowerCase();
  return n === 'vida' || n.includes('vida');
}

function codigoEstipulanteEhVida(codigo: string | null | undefined): boolean {
  const digits = String(codigo ?? '').replace(/\D/g, '');
  if (!digits) {
    return false;
  }
  const v = Number(digits);
  return Number.isFinite(v) && v === VIDA_APOLICE_RAMO_CODE;
}

/**
 * Should call `gerar-protocolo-atendimento`: ramo **Vida** allocated in the estipulante (`codigo` 4 or `nome` vida),
 * or — without vinculo passed — fallback by the policy line (`nomeRamoApol` or `codRamoApol === 4` only).
 */
export function shouldSyncVidaAttendanceProtocol(
  policyRow: UserPoliciesListItem | null | undefined,
  estipulanteRamo?: EstipulanteRamoForProtocol | null,
): boolean {
  if (policyRow == null) {
    return false;
  }

  if (estipulanteRamo != null) {
    const hasCod = String(estipulanteRamo.codigo ?? '').trim() !== '';
    const hasNome = String(estipulanteRamo.nome ?? '').trim() !== '';
    if (hasCod || hasNome) {
      if (codigoEstipulanteEhVida(estipulanteRamo.codigo)) {
        return true;
      }
      if (nomeIndicaVida(estipulanteRamo.nome)) {
        return true;
      }
      return false;
    }
  }

  if (nomeIndicaVida(policyRow.nomeRamoApol)) {
    return true;
  }
  const n = Number(policyRow.codRamoApol);
  return Number.isFinite(n) && n === VIDA_APOLICE_RAMO_CODE;
}

/** @deprecated Prefer {@link shouldSyncVidaAttendanceProtocol} with estipulante ramo. */
export function isVidaRamo4PolicyRow(row: UserPoliciesListItem | null | undefined): boolean {
  return shouldSyncVidaAttendanceProtocol(row, undefined);
}

export function resolveIdentContratoForVidaProtocol(row: UserPoliciesListItem): number | undefined {
  const fromApol = row.identContratoApol;
  if (fromApol != null && Number.isFinite(Number(fromApol))) {
    return Number(fromApol);
  }
  const fromContrato = row.identContrato;
  if (fromContrato != null && Number.isFinite(Number(fromContrato))) {
    return Number(fromContrato);
  }
  return undefined;
}

/**
 * After List apólices: if the context is **Vida** (`shouldSyncVidaAttendanceProtocol`), by default call
 * `gerar-protocolo-atendimento` e persiste na store e no `localStorage`.
 * With `options.skipRemoteProtocolRequest` and `vinculoItem`, only hydrate the store from `localStorage` (e.g. `useCompany` auto-restore of persisted vínculo after reload — not the card click).
 */
export function useCompanyVidaProtocolAtendimento() {
  const syncProtocolAfterPolicyRow = useCallback(
    async (
      policyRow: UserPoliciesListItem | null | undefined,
      codUsuarioInclusao: number | null | undefined,
      estipulanteRamo?: EstipulanteRamoForProtocol | null,
      options?: SyncProtocolAfterPolicyRowOptions | null,
    ): Promise<void> => {
      const {
        clear,
        setLoading,
        setError,
        applySuccessfulResponse,
        applyRemoteProtocolFetchFailure,
        hydrateFromPersistedStorageIfRamo4Vinculo,
      } = useVidaProtocolAtendimentoStore.getState();

      if (policyRow == null || !shouldSyncVidaAttendanceProtocol(policyRow, estipulanteRamo)) {
        clear();
        return;
      }

      if (options?.skipRemoteProtocolRequest === true && options.vinculoItem != null) {
        hydrateFromPersistedStorageIfRamo4Vinculo(options.vinculoItem);
        return;
      }

      if (codUsuarioInclusao == null || !Number.isFinite(codUsuarioInclusao)) {
        clear();
        return;
      }

      const identContrato = resolveIdentContratoForVidaProtocol(policyRow);
      if (identContrato == null) {
        clear();
        setError('Identificador de contrato da apólice indisponível para gerar protocolo.');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await apiService.protocol.getGenerateAttendanceProtocol(
          identContrato,
          codUsuarioInclusao,
        );
        applySuccessfulResponse(response);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Falha ao gerar protocolo de atendimento.';
        applyRemoteProtocolFetchFailure(msg);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { syncProtocolAfterPolicyRow };
}
